// frontend/.../public/vendorPrequal/highRiskSchema.ts
// Declarative schema for HSE (Part 4), Quality Systems (Part 5) and Industrial
// Relations (Part 6) questionnaires. The labels here mirror the Jotform
// source form 1:1 so submissions carry the same meaning as the legacy process.
//
// The schema drives:
//   - the public form renderer (<HighRiskForm />)
//   - the admin detail read-only renderer (in VendorPrequalDetail)
//   - a human-readable dump in the generated PDF (backend mirrors this file's labels)

export type HighRiskField =
  | { type: 'yesno'; key: string; label: string; uploadKey?: string; detailsKey?: string }
  | { type: 'textarea'; key: string; label: string; uploadKey?: string }
  | { type: 'person'; key: string; label: string }
  | { type: 'upload'; key: string; label: string; multiple?: boolean; accept?: string };

export interface HighRiskGroup {
  title?: string;
  fields: HighRiskField[];
}

export interface HighRiskSection {
  title: string;
  intro?: string;
  groups: HighRiskGroup[];
}

export interface HighRiskPart {
  key: 'hse' | 'quality' | 'ir';
  title: string;
  sections: HighRiskSection[];
}

// ----------------------------------------------------------------------------
// Part 4 — HSE
// ----------------------------------------------------------------------------
const HSE: HighRiskPart = {
  key: 'hse',
  title: 'Part 4 — Health, Safety and Environment (HSE)',
  sections: [
    {
      title: 'HSE Section 1: Leadership and Commitment',
      intro: 'Commitment to HSE through leadership',
      groups: [
        {
          fields: [
            {
              type: 'yesno',
              key: 'hse.s1.seniorInvolved',
              label: 'Are senior managers personally involved in HSE management?',
              uploadKey: 'hse.s1.seniorInvolved.upload',
            },
            {
              type: 'yesno',
              key: 'hse.s1.commitment',
              label: 'Is there evidence of commitment at all levels of the organization?',
              uploadKey: 'hse.s1.commitment.upload',
            },
            {
              type: 'yesno',
              key: 'hse.s1.positiveCulture',
              label: 'Is there a positive culture towards HSE matters?',
              uploadKey: 'hse.s1.positiveCulture.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'HSE Section 2: Policy and Strategic Objectives',
      groups: [
        {
          title: 'HSE policy document',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s2.hasPolicy',
              label: 'Does your Company have an HSE policy document?',
              uploadKey: 'hse.s2.policy.upload',
            },
            {
              type: 'person',
              key: 'hse.s2.overallResponsibility',
              label:
                'Who has overall and final responsibility for HSE in your organization? (Provide name, title and experience.)',
            },
            {
              type: 'person',
              key: 'hse.s2.seniorOnsite',
              label:
                'Who is the most senior person in the organization responsible for this policy being carried out on site where employees are working? (Provide name, title and experience.)',
            },
            {
              type: 'yesno',
              key: 'hse.s2.itemiseMethods',
              label:
                'Itemise the methods by which you have drawn your HSE policy document to the attention of all your employees. (Provide evidence where applicable.)',
              uploadKey: 'hse.s2.itemiseMethods.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s2.advisingChanges',
              label: 'What are your arrangements for advising employees of changes in the policy?',
              uploadKey: 'hse.s2.advisingChanges.upload',
            },
          ],
        },
      ],
    },
    {
      title:
        'HSE Section 3: Organization, Responsibilities, Resources, Standards and Documentation',
      groups: [
        {
          title: 'Commitment and communication',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s3.mgmtInvolvement',
              label:
                'How is management involved in HSE activities, objective-setting and monitoring?',
              uploadKey: 'hse.s3.mgmtInvolvement.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.structure',
              label: 'How is your Company structured to manage and communicate HSE effectively?',
              uploadKey: 'hse.s3.structure.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.communicationMeetings',
              label:
                'What provision does your Company make for HSE communication meetings? (Provide evidence where applicable)',
              uploadKey: 'hse.s3.communicationMeetings.upload',
            },
          ],
        },
        {
          title: 'Competence and training of managers / supervisors / senior site staff / HSE advisers',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s3.formalTraining',
              label:
                'Have the managers and supervisors at all levels received formal HSE training in their responsibilities with respect to conducting work to HSE standards?',
            },
            {
              type: 'textarea',
              key: 'hse.s3.trainingDetails',
              label:
                'Please give details and, where the training is given in-house, please describe the content and duration of courses.',
              uploadKey: 'hse.s3.trainingDetails.upload',
            },
          ],
        },
        {
          title: 'Competence and General HSE training',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s3.newEmpWorkHse',
              label:
                'What arrangements does your Company have to ensure new employees have knowledge of work related HSE issues, and to keep this knowledge up to date?',
              uploadKey: 'hse.s3.newEmpWorkHse.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.newEmpPolicies',
              label:
                'What arrangements does your Company have to ensure new employees have knowledge of your specific HSE policies and practices?',
              uploadKey: 'hse.s3.newEmpPolicies.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.newEmpHazards',
              label:
                'What arrangements does your Company have to ensure new employees have been instructed and have received information on any specific hazards arising out of their work scope?',
              uploadKey: 'hse.s3.newEmpHazards.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.allEmpAware',
              label:
                'What training do you provide to ensure that all employees are aware of Company HSE requirements?',
              uploadKey: 'hse.s3.allEmpAware.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.existingStaff',
              label:
                'What arrangements does your Company have to ensure that the HSE knowledge of existing staff is up to date?',
              uploadKey: 'hse.s3.existingStaff.upload',
            },
          ],
        },
        {
          title: 'Specialised training',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s3.specialisedAreas',
              label:
                'Have you identified areas of your Company operations where specialised training is required to deal with major hazards?',
              detailsKey: 'hse.s3.specialisedAreas.details',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardRoadTransport',
              label: 'Specialised hazard: Road transport — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardRoadTransport.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardWorkingInHeat',
              label: 'Specialised hazard: Working in heat — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardWorkingInHeat.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardCampHygiene',
              label: 'Specialised hazard: Camp hygiene — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardCampHygiene.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardMultiCultural',
              label:
                'Specialised hazard: Communication to a multi-cultural workforce — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardMultiCultural.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardRadiation',
              label: 'Specialised hazard: Radiation — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardRadiation.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardChemical',
              label:
                'Specialised hazard: Chemical handling — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardChemical.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardAsbestos',
              label: 'Specialised hazard: Asbestos — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardAsbestos.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.hazardWasteDisposal',
              label:
                'Specialised hazard: Waste Disposal — how identified, assessed and controlled?',
              uploadKey: 'hse.s3.hazardWasteDisposal.upload',
            },
          ],
        },
        {
          title: 'HSE qualified staff - additional training',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s3.staffTrainingY',
              label: 'Is HSE training conducted by any of your staff?',
            },
            {
              type: 'textarea',
              key: 'hse.s3.staffQualifications',
              label: 'What are their qualifications?',
              uploadKey: 'hse.s3.staffQualifications.upload',
            },
          ],
        },
        {
          title: 'Assessment of suitability of subcontractors / other companies',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s3.subcontractorCompetence',
              label: 'For your sub-contractors, how do you assess HSE competence?',
              uploadKey: 'hse.s3.subcontractorCompetence.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.subcontractorStandards',
              label: 'How do you ensure your sub-contractors meet your HSE standards?',
              uploadKey: 'hse.s3.subcontractorStandards.upload',
            },
          ],
        },
        {
          title: 'Standards',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s3.recordStandards',
              label: 'Where do you record your HSE standards?',
              uploadKey: 'hse.s3.recordStandards.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.standardsVerify',
              label: 'How do you ensure these are met and verified?',
              uploadKey: 'hse.s3.standardsVerify.upload',
            },
            {
              type: 'yesno',
              key: 'hse.s3.standardsSystem',
              label: 'Is there a system for producing, updating and disseminating standards?',
              detailsKey: 'hse.s3.standardsSystem.details',
              uploadKey: 'hse.s3.standardsSystem.upload',
            },
          ],
        },
        {
          title: 'Personnel Competency',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s3.competencyProcess',
              label: 'Describe your system or process for personnel competency in your company.',
              uploadKey: 'hse.s3.competencyProcess.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s3.competencyApplication',
              label:
                'Demonstrate the application of the competency requirements to personnel that are working or will be working on the provision of services, personnel or equipment to GRPS.',
              uploadKey: 'hse.s3.competencyApplication.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'HSE Section 4: Hazards and Effects Management',
      groups: [
        {
          title: 'Hazards and effects assessment',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s4.hazardTechniques',
              label:
                'What techniques are used within your Company for the identification, assessment, control and recovery of hazards and effects?',
              uploadKey: 'hse.s4.hazardTechniques.upload',
            },
          ],
        },
        {
          title: 'Exposure of the workforce',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s4.monitorExposure',
              label:
                'Do you have in place any systems to monitor the exposure of your workforce to chemical or physical agents?',
              detailsKey: 'hse.s4.monitorExposure.details',
              uploadKey: 'hse.s4.monitorExposure.upload',
            },
          ],
        },
        {
          title: 'Handling of chemicals',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s4.chemicalProperties',
              label:
                'How is your workforce advised on the properties of chemicals encountered in the course of their work?',
              uploadKey: 'hse.s4.chemicalProperties.upload',
            },
          ],
        },
        {
          title: 'Personal protective equipment',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s4.ppe',
              label:
                'What arrangements does your Company have for provision and upkeep of protective clothing, both standard issue, and that required for specialised activities?',
              uploadKey: 'hse.s4.ppe.upload',
            },
          ],
        },
        {
          title: 'Waste management',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s4.wasteSystems',
              label:
                'Does your Company have in place systems for identification, classification and management of waste?',
              uploadKey: 'hse.s4.wasteSystems.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'HSE Section 5: Planning and Procedures',
      groups: [
        {
          title: 'HSE or operations manuals',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s5.hseManual',
              label:
                'Do you have a Company HSE manual (or Operations manual with relevant sections on HSE) which describes in detail your Company approved HSE working practices relating to your work activities?',
              uploadKey: 'hse.s5.hseManual.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s5.workingPractices',
              label:
                'How do you ensure that the working practices and procedures used by your employees on-site are consistently in accordance with your HSE policy objectives and arrangements?',
              uploadKey: 'hse.s5.workingPractices.upload',
            },
          ],
        },
        {
          title: 'Equipment control and maintenance',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s5.plantEquipment',
              label:
                'How do you ensure that plant and equipment used within your premises, on-site, or at other locations, by your employees are correctly registered, controlled and maintained in a safe working condition?',
              uploadKey: 'hse.s5.plantEquipment.upload',
            },
          ],
        },
        {
          title: 'Road Safety Management',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s5.roadVehicle',
              label:
                'What arrangements does your Company have for combating road and vehicle incidents?',
              uploadKey: 'hse.s5.roadVehicle.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'HSE Section 6: Implementation and Performance Monitoring',
      groups: [
        {
          title: 'Management and performance monitoring of work activities',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s6.supervision',
              label:
                'What arrangements does your Company have for supervision and monitoring of performance?',
              uploadKey: 'hse.s6.supervision.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.criteria',
              label: 'What type of performance criteria is used in your Company? (Give examples.)',
              uploadKey: 'hse.s6.criteria.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.passToMgmt',
              label:
                'What arrangements does your Company have for passing on any results and findings of this supervision and monitoring to your management?',
              uploadKey: 'hse.s6.passToMgmt.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.passToSite',
              label:
                'What arrangements does your Company have for passing on any results and findings of this supervision and monitoring to your site employees?',
              uploadKey: 'hse.s6.passToSite.upload',
            },
          ],
        },
        {
          title: 'HSE performance achievement awards',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s6.awards',
              label: 'Has your Company received any award for HSE performance achievement?',
              detailsKey: 'hse.s6.awards.details',
              uploadKey: 'hse.s6.awards.upload',
            },
          ],
        },
        {
          title: 'Statutory notifiable incidents / dangerous occurrences',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s6.notifiableIncidents',
              label:
                'Has your Company suffered any statutory notifiable incidents in the last five years (relating to safety, occupational health and environmental)?',
              detailsKey: 'hse.s6.notifiableIncidents.details',
              uploadKey: 'hse.s6.notifiableIncidents.upload',
            },
          ],
        },
        {
          title: 'Improvement requirement and prohibition notices',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s6.improvementNotices',
              label:
                'Has your Company suffered any improvement requirement or prohibition notices by the relevant national body, regulatory body, for HSE or other enforcing authority or been prosecuted under any HSE legislation?',
              detailsKey: 'hse.s6.improvementNotices.details',
              uploadKey: 'hse.s6.improvementNotices.upload',
            },
          ],
        },
        {
          title: 'HSE performance records',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s6.recordsMaintained',
              label:
                'Have you maintained records of your incidents and HSE performance for the past five years?',
              detailsKey: 'hse.s6.recordsMaintained.details',
              uploadKey: 'hse.s6.recordsMaintained.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.healthRecorded',
              label: 'How is health performance recorded?',
              uploadKey: 'hse.s6.healthRecorded.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.envRecorded',
              label: 'How is environmental performance recorded?',
              uploadKey: 'hse.s6.envRecorded.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.performanceReview',
              label: 'How often is HSE performance reviewed? By whom?',
              uploadKey: 'hse.s6.performanceReview.upload',
            },
          ],
        },
        {
          title: 'Incident investigation and reporting',
          fields: [
            {
              type: 'textarea',
              key: 'hse.s6.investigators',
              label: 'Who conducts incident investigations?',
              uploadKey: 'hse.s6.investigators.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s6.findingsCommunicated',
              label:
                'How are the findings following an investigation, or relevant incident occurring elsewhere, communicated to your employees?',
              uploadKey: 'hse.s6.findingsCommunicated.upload',
            },
            {
              type: 'yesno',
              key: 'hse.s6.nearMisses',
              label: 'Are near misses reported?',
              uploadKey: 'hse.s6.nearMisses.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'HSE Section 7: Auditing and Review',
      groups: [
        {
          title: 'Auditing',
          fields: [
            {
              type: 'yesno',
              key: 'hse.s7.auditProcedure',
              label:
                'Do you have a written procedure on HSE auditing and how does this specify the standards for auditing?',
              uploadKey: 'hse.s7.auditProcedure.upload',
            },
            {
              type: 'yesno',
              key: 'hse.s7.auditPlans',
              label:
                'Do your Company’s HSE Plans include schedules for auditing and what range of auditing is covered?',
              uploadKey: 'hse.s7.auditPlans.upload',
            },
            {
              type: 'textarea',
              key: 'hse.s7.auditEffectiveness',
              label:
                'How does your company verify the effectiveness of the auditing, and how does management report and follow up audits?',
              uploadKey: 'hse.s7.auditEffectiveness.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'HSE Section 8: HSE Management - Additional Features',
      groups: [
        {
          fields: [
            {
              type: 'yesno',
              key: 'hse.s8.membership',
              label:
                'Does your Company hold membership of any industry, trade or HSE organisation?',
              uploadKey: 'hse.s8.membership.upload',
            },
            {
              type: 'yesno',
              key: 'hse.s8.additionalFeatures',
              label:
                'Does your Company have any other HSE features or arrangements not described elsewhere in your response to the questionnaire?',
              uploadKey: 'hse.s8.additionalFeatures.upload',
            },
          ],
        },
      ],
    },
  ],
};

// ----------------------------------------------------------------------------
// Part 5 — Quality Systems
// ----------------------------------------------------------------------------
const QUALITY: HighRiskPart = {
  key: 'quality',
  title: 'Part 5 — Quality Systems',
  sections: [
    {
      title: 'Quality Section 1: Leadership and Commitment',
      intro: 'Commitment to Quality through leadership',
      groups: [
        {
          fields: [
            {
              type: 'yesno',
              key: 'quality.s1.seniorInvolved',
              label: 'Are senior managers personally involved in quality management?',
              uploadKey: 'quality.s1.seniorInvolved.upload',
            },
            {
              type: 'yesno',
              key: 'quality.s1.commitment',
              label: 'Is there evidence of commitment at all levels of the organization?',
              uploadKey: 'quality.s1.commitment.upload',
            },
            {
              type: 'yesno',
              key: 'quality.s1.positiveCulture',
              label: 'Is there a positive culture towards quality matters?',
              uploadKey: 'quality.s1.positiveCulture.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'Quality Section 2: Policy and Strategic Objectives',
      groups: [
        {
          title: 'Quality policy document',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s2.hasPolicy',
              label: 'Does your Company have a quality policy document?',
              uploadKey: 'quality.s2.policy.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s2.overallResponsibility',
              label: 'Who has overall and final responsibility for quality in your organisation?',
              uploadKey: 'quality.s2.overallResponsibility.upload',
            },
          ],
        },
        {
          title: 'Employee access to quality policy document',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s2.itemiseMethods',
              label:
                'Itemise the methods by which you have drawn your quality policy document to the attention of all your employees.',
              uploadKey: 'quality.s2.itemiseMethods.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s2.advisingChanges',
              label: 'What are your arrangements for advising employees of changes in the policy?',
              uploadKey: 'quality.s2.advisingChanges.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'Quality Section 3: Organisation, Responsibilities, Resources, Standards and Documentation',
      groups: [
        {
          title: 'Commitment and communication',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s3.mgmtInvolvement',
              label:
                'How is management involved in quality activities, objective-setting and monitoring?',
              uploadKey: 'quality.s3.mgmtInvolvement.upload',
            },
          ],
        },
        {
          title: 'Competence and training of managers / supervisors / senior site staff / quality advisers',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s3.formalTraining',
              label:
                'Do managers and supervisors receive formal quality training with respect to conducting work to quality standards?',
            },
            {
              type: 'textarea',
              key: 'quality.s3.trainingDetailsA',
              label:
                'Where the training is given in-house, please describe the content and duration of courses.',
              uploadKey: 'quality.s3.trainingDetailsA.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.trainingDetailsB',
              label:
                'Where the training is given in-house, please describe the content and duration of courses.',
              uploadKey: 'quality.s3.trainingDetailsB.upload',
            },
          ],
        },
        {
          title: 'Competence and general quality training',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s3.newEmpWork',
              label:
                'What arrangements does your Company have to ensure new employees have knowledge of work related quality issues, and to keep this knowledge up to date?',
              uploadKey: 'quality.s3.newEmpWork.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.newEmpPolicies',
              label:
                'What arrangements does your Company have to ensure new employees have knowledge of your specific quality policies and practices?',
              uploadKey: 'quality.s3.newEmpPolicies.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.newEmpHazards',
              label:
                'What arrangements does your Company have to ensure new employees have been instructed and have received information on any specific quality hazards arising out of their work scope?',
              uploadKey: 'quality.s3.newEmpHazards.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.existingStaff',
              label:
                'What arrangements does your Company have to ensure that the quality knowledge of existing staff is up to date?',
              uploadKey: 'quality.s3.existingStaff.upload',
            },
          ],
        },
        {
          title: 'Quality qualified staff - additional training',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s3.staffTrainingY',
              label: 'Does any of your staff conduct quality training?',
            },
            {
              type: 'textarea',
              key: 'quality.s3.staffQualifications',
              label: 'What are their qualifications?',
              uploadKey: 'quality.s3.staffQualifications.upload',
            },
          ],
        },
        {
          title: 'Assessment of suitability of subcontractors / other companies',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s3.subcontractorPerformance',
              label: 'For your sub-contractors, how do you assess quality performance?',
              uploadKey: 'quality.s3.subcontractorPerformance.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.subcontractorStandards',
              label:
                'How do you ensure your sub-contractors meet your quality standards on an ongoing basis?',
              uploadKey: 'quality.s3.subcontractorStandards.upload',
            },
          ],
        },
        {
          title: 'Standards',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s3.recordStandards',
              label: 'Where do you record your quality standards?',
              uploadKey: 'quality.s3.recordStandards.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.standardsVerify',
              label: 'How do you ensure these are met and verified?',
              uploadKey: 'quality.s3.standardsVerify.upload',
            },
            {
              type: 'yesno',
              key: 'quality.s3.standardsSystem',
              label: 'Is there a system for producing, updating and disseminating standards?',
              uploadKey: 'quality.s3.standardsSystem.upload',
            },
          ],
        },
        {
          title: 'Personnel competency',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s3.competencyProcess',
              label:
                'Describe your system or process for personnel quality competency in your company.',
              uploadKey: 'quality.s3.competencyProcess.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s3.competencyApplication',
              label:
                'Demonstrate the application of the quality competency requirements to personnel that are working or will be working on the provision of services, personnel or equipment to GRPS projects.',
              uploadKey: 'quality.s3.competencyApplication.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'Quality Section 4: Hazards and Effects Management',
      groups: [
        {
          title: 'Quality hazards and effects assessment',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s4.hazardTechniques',
              label:
                'What techniques are used within your Company for the identification, assessment, control and recovery of quality hazards and effects?',
              uploadKey: 'quality.s4.hazardTechniques.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'Quality Section 5: Implementation and Performance Monitoring',
      groups: [
        {
          title: 'Management and performance monitoring of work activities',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s5.supervision',
              label:
                'What arrangements does your Company have for supervision and monitoring of quality performance?',
              uploadKey: 'quality.s5.supervision.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s5.criteria',
              label: 'What type of quality performance criteria are used in your Company?',
              uploadKey: 'quality.s5.criteria.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s5.passToMgmt',
              label:
                'What arrangements does your Company have for passing on any results and findings of this supervision and monitoring to your Management?',
              uploadKey: 'quality.s5.passToMgmt.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s5.passToSite',
              label:
                'What arrangements does your Company have for passing on any results and findings of this supervision and monitoring to your Site employees?',
              uploadKey: 'quality.s5.passToSite.upload',
            },
          ],
        },
        {
          title: 'Quality performance achievement awards',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s5.awards',
              label: 'Has your Company received any award for quality performance achievement?',
              uploadKey: 'quality.s5.awards.upload',
            },
          ],
        },
        {
          title: 'Quality performance records',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s5.recordsMaintained',
              label:
                'Have you maintained records of your incidents and quality performance for the last five years?',
              detailsKey: 'quality.s5.recordsMaintained.details',
              uploadKey: 'quality.s5.recordsMaintained.upload',
            },
          ],
        },
        {
          title: 'Incident investigation and reporting',
          fields: [
            {
              type: 'textarea',
              key: 'quality.s5.investigators',
              label: 'Who conducts quality incident investigations?',
              uploadKey: 'quality.s5.investigators.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s5.findingsCommunicated',
              label:
                'How are the findings following an investigation, or a relevant quality incident occurring elsewhere, communicated to your employees?',
              uploadKey: 'quality.s5.findingsCommunicated.upload',
            },
            {
              type: 'yesno',
              key: 'quality.s5.nearMisses',
              label: 'Are quality near misses reported?',
              uploadKey: 'quality.s5.nearMisses.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'Quality Section 6: Auditing and Review',
      groups: [
        {
          title: 'Auditing',
          fields: [
            {
              type: 'yesno',
              key: 'quality.s6.auditProcedure',
              label: 'Do you have a written procedure on quality auditing?',
              uploadKey: 'quality.s6.auditProcedure.upload',
            },
            {
              type: 'textarea',
              key: 'quality.s6.auditStandards',
              label: 'How does this specify the standards for auditing?',
              uploadKey: 'quality.s6.auditStandards.upload',
            },
          ],
        },
      ],
    },
  ],
};

// ----------------------------------------------------------------------------
// Part 6 — Industrial Relations
// ----------------------------------------------------------------------------
const IR: HighRiskPart = {
  key: 'ir',
  title: 'Part 6 — Industrial Relations (IR)',
  sections: [
    {
      title: 'IR Section 1: Industrial Regulation',
      groups: [
        {
          title: 'Management tools for Industrial Regulation',
          fields: [
            {
              type: 'textarea',
              key: 'ir.s1.industrialInstrument',
              label:
                'What is the name of the industrial instrument your Company intends to engage its employees under for the duration of these proposed works? (e.g. Common Law Contract, Modern Award, Industrial Agreement)',
              uploadKey: 'ir.s1.industrialInstrument.upload',
            },
            {
              type: 'yesno',
              key: 'ir.s1.fwcAgreement',
              label: 'Do you have a Fair Work Commission (FWC) approved industrial agreement?',
              uploadKey: 'ir.s1.fwcAgreement.upload',
            },
            {
              type: 'yesno',
              key: 'ir.s1.ermp',
              label: 'Does your Company have an Employee Relations Management Plan (ERMP)?',
              uploadKey: 'ir.s1.ermp.upload',
            },
            {
              type: 'textarea',
              key: 'ir.s1.responsiblePerson',
              label:
                'Please provide the details of the person within your Company primarily responsible for Industrial Relations. (Provide names, title and experience, along with Contact Details)',
            },
            {
              type: 'yesno',
              key: 'ir.s1.externalAdvisors',
              label:
                'Does your Company have any external Industrial Relations advisors (e.g. law firms, employer associations, professional advisors, etc.)?',
              detailsKey: 'ir.s1.externalAdvisors.details',
            },
            {
              type: 'textarea',
              key: 'ir.s1.performanceReview',
              label: 'How often is quality performance reviewed? By whom?',
              uploadKey: 'ir.s1.performanceReview.upload',
            },
          ],
        },
        {
          title: 'Industrial Action',
          fields: [
            {
              type: 'yesno',
              key: 'ir.s1.industrialAction',
              label:
                'Please list any industrial action that you have experienced in your Company over the last 3 years, including reason and number of hours lost.',
              detailsKey: 'ir.s1.industrialAction.details',
              uploadKey: 'ir.s1.industrialAction.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'IR Section 2: Union Functions',
      groups: [
        {
          fields: [
            {
              type: 'textarea',
              key: 'ir.s2.rightOfEntry',
              label:
                'How does your Company intend to manage Right of Entry for union officials in relations to the works?',
              uploadKey: 'ir.s2.rightOfEntry.upload',
            },
            {
              type: 'textarea',
              key: 'ir.s2.primaryUnions',
              label: 'What are the primary unions that your Company deals with?',
            },
          ],
        },
      ],
    },
    {
      title: 'IR Section 3: Industrial Relations Procedures & Systems',
      groups: [
        {
          title: 'Management tools for Industrial Relations',
          fields: [
            {
              type: 'yesno',
              key: 'ir.s3.disputeResolution',
              label:
                'Does your Company have a Dispute Resolution Procedure? (e.g. Common Law Contract, Modern Award, Industrial Agreement)',
              uploadKey: 'ir.s3.disputeResolution.upload',
            },
            {
              type: 'textarea',
              key: 'ir.s3.preventAction',
              label:
                'What systems will your Company have in place to ensure no industrial action takes place during the duration of the works?',
              uploadKey: 'ir.s3.preventAction.upload',
            },
          ],
        },
      ],
    },
    {
      title: 'IR Section 4: Resources',
      groups: [
        {
          title: 'Resources Planning',
          fields: [
            {
              type: 'yesno',
              key: 'ir.s4.labourHire',
              label:
                'Do you intend to source labour from labour hire suppliers or other companies?',
              uploadKey: 'ir.s4.labourHire.upload',
            },
            {
              type: 'textarea',
              key: 'ir.s4.localWorkforce',
              label: 'How many of your workforce will be engaged locally for the works?',
              uploadKey: 'ir.s4.localWorkforce.upload',
            },
          ],
        },
      ],
    },
  ],
};

export const HIGH_RISK_PARTS: HighRiskPart[] = [HSE, QUALITY, IR];
